import React, { useState, useEffect, useMemo } from "react";
import { Button, Badge, DataTable, clientSortRows } from "./ui";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "./Modals/ConfirmModal";
import UploadFileModal from "./Modals/UploadFileModal";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBInput, MDBDropdown, MDBDropdownToggle, MDBDropdownMenu, MDBDropdownItem } from "mdb-react-ui-kit";
import { api } from "../services";
import { useTranslation } from "react-i18next";

const ITEMS_PER_PAGE = 10;

function Items() {
  const { t } = useTranslation("translation");
  const [viewMode, setViewMode] = useState("list");
  const [itemsData, setItemsData] = useState([]); // Fetched or Saved Items
  const [savedItems, setSavedItems] = useState([]); // Backup Items (Saved in DB)
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState("Select App");
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [modalData, setModalData] = useState({
    title: "",
    message: "",
    action: () => {},
    actionData: null,
    visibility: null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [tableSortColumn, setTableSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  console.log(showConfirm);

  const navigate = useNavigate();

  useEffect(() => {
    fetchApps();
    fetchItems();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedAppId) {
      fetchItems(selectedAppId);
    }
    // eslint-disable-next-line
  }, [selectedAppId]);

  useEffect(() => {
    if (!showModal) {
      fetchItems();
    }
    // eslint-disable-next-line
  }, [showModal]);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line
  }, [currentPage]);

  // Fetch Apps
  const fetchApps = async () => {
    try {
      const token = localStorage.getItem("token");
      console.log("Token", token);
      if (!token) {
        setError(t("Unauthorized: Please log in."));
        navigate("/login");
        return;
      }

      const response = await api.get("/apps", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApps(response.data.apps || []);
    } catch (error) {
      console.error(
        "Error fetching apps:",
        error.response?.data || error.message
      );
      setError(t("Failed to fetch apps."));
    }
  };

  // Fetch Catalog Items (Try API First, If Fails Use Saved Data)
  const fetchItems = async (appId = null) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError(t("Unauthorized: Please log in."));
        navigate("/login");
        return;
      }
      let endpoint = appId
        ? `/apps/${appId}/catalog/items?page=${currentPage}&limit=${ITEMS_PER_PAGE}`
        : `/catalog/items?page=${currentPage}&limit=${ITEMS_PER_PAGE}`;

      const response = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setItemsData(response.data.items || []);
      setSavedItems(response.data.items || []); // ✅ Save Fetched Data in Backup

      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems ?? (response.data.totalPages || 1) * ITEMS_PER_PAGE);
    } catch (error) {
      console.error(
        "Error fetching catalog items:",
        error.response?.data || error.message
      );
      setError(t("Failed to fetch catalog items. Showing saved items."));

      // ✅ If Fetch Fails, Use Last Saved Items
      if (savedItems.length > 0) {
        setItemsData(savedItems);
      }

      // ✅ If Token is Expired, Redirect to Login
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    }
  };

  // Handle App Selection
  const handleAppSelect = (app) => {
    setSelectedApp(app.application);
    setSelectedAppId(app._id);
    fetchItems(app._id);
  };

  // Handle Search Input Change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle View Mode Change
  const handleViewModeChange = (mode) => setViewMode(mode);

  // Handle Add Item Click
  const handleAddItemClick = () => navigate("/add-product");

  // Handle Edit Item Click
  const handleEditItemClick = (item) => {
    navigate(`/edit-product/${item.item_id}`);
  };

  //Handle visibility of the Product
  const handleVisibility = async (item, visibility) => {
    console.log("Confirmed!", item);
    const payload = {
      productId: item._id,
      isVisible: visibility,
    };

    try {
      const token = localStorage.getItem("token");
      console.log("🔄 Sending Update visibility Payload:", payload);

      //  Correct API URL
      const response = await api.post(`hide-product`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log(" Item visibility updated Successfully:", response.data);

      await fetchItems();
    } catch (error) {
      console.error(
        " Error adding category:",
        error.response?.data || error.message
      );
      setError(error.response?.data?.message || t("Failed to add category."));
    }

    setShowConfirm(false); // Hide modal after confirming
  };

  // Filter Items for Display
  const filteredItems = itemsData.filter(
    (item) =>
      item.name?.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.category_id?.name?.en
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  const sortedItems = useMemo(
    () =>
      clientSortRows(filteredItems, tableSortColumn, sortDirection, (item, col) => {
        switch (col) {
          case "name":
            return (item.name?.en || "").toLowerCase();
          case "category":
            return (item.category_id?.name?.en || "").toLowerCase();
          case "price":
            return item.price ?? 0;
          case "visibility":
            return item.is_visible ? 1 : 0;
          default:
            return "";
        }
      }),
    [filteredItems, tableSortColumn, sortDirection]
  );

  const columns = useMemo(
    () => [
      {
        id: "image",
        header: "Item Image",
        render: (item) => (
          <img
            src={item.image_path || "https://via.placeholder.com/100"}
            alt={item.name?.en}
            className="img-fluid"
            style={{ maxHeight: "50px", objectFit: "cover" }}
          />
        ),
      },
      {
        id: "name",
        header: "Item Name",
        sortable: true,
        strong: true,
        render: (item) => item.name?.en || "No Name",
      },
      {
        id: "category",
        header: "Category",
        sortable: true,
        render: (item) => item.category_id?.name?.en || "No Category",
      },
      {
        id: "price",
        header: "Price",
        sortable: true,
        render: (item) => `SAR ${item.price?.toFixed(2) || "0.00"}`,
      },
      {
        id: "visibility",
        header: "Visibility",
        sortable: true,
        render: (item) =>
          item.is_visible ? (
            <Badge color="success" light>
              VISIBLE
            </Badge>
          ) : (
            <Badge color="warning" light>
              HIDDEN
            </Badge>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        hideable: false,
        render: (item) => (
          <div onClick={(e) => e.stopPropagation()}>
            {item.is_visible ? (
              <Button
                color="warning"
                outline
                size="sm"
                onClick={() =>
                  setModalData({
                    title: "Hide Item",
                    message: "Do you want to hide this item?",
                    action: handleVisibility,
                    actionData: item,
                    visibility: false,
                  }) || setShowConfirm(true)
                }
              >
                HIDE
              </Button>
            ) : (
              <Button
                color="primary"
                outline
                size="sm"
                onClick={() =>
                  setModalData({
                    title: "Un Hide Item",
                    message: "Do you want to unhide this item?",
                    action: handleVisibility,
                    actionData: item,
                    visibility: true,
                  }) || setShowConfirm(true)
                }
              >
                UNHIDE
              </Button>
            )}
          </div>
        ),
      },
    ],
    []
  );

  return (
    <MDBContainer fluid className="p-4 content-area flex-grow-1">
      <h2 className="mb-4">Catalog Items</h2>

      {/* Display Error Message */}
      {error && <p className="text-danger">{error}</p>}

      {/* App Selection */}
      <MDBRow className="mb-4">
        <MDBCol md="6">
          <label>Select App</label>
          <MDBDropdown>
            <MDBDropdownToggle color="light">{selectedApp}</MDBDropdownToggle>
            <MDBDropdownMenu>
              {apps.map((app) => (
                <MDBDropdownItem
                  key={app._id}
                  onClick={() => handleAppSelect(app)}
                >
                  {app.application}
                </MDBDropdownItem>
              ))}
            </MDBDropdownMenu>
          </MDBDropdown>
        </MDBCol>
      </MDBRow>

      {/* Quick Actions */}
      <MDBRow className="mb-4">
        <h6>Quick Actions</h6>
        <MDBCol>
          <Button
            color="primary"
            className="quick-action-btn me-2"
            onClick={handleAddItemClick}
          >
            Add New Item
          </Button>

          <Button
            onClick={() => setShowModal(true)}
            color="primary"
            className="quick-action-btn me-2"
          >
            Import From File
          </Button>
          <UploadFileModal
            show={showModal}
            handleClose={() => setShowModal(false)}
            type={"products"}
          />
        </MDBCol>
      </MDBRow>

      {/* Search and View Toggle */}
      <MDBRow className="mb-4 d-flex align-items-center">
        <MDBCol md="6">
          <MDBInput
            type="text"
            placeholder="Search Items"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </MDBCol>
        <MDBCol
          md="6"
          className="d-flex justify-content-end align-items-center"
        >
          <div className="ms-3">
            <Button
              color="light"
              onClick={() => handleViewModeChange("list")}
              active={viewMode === "list"}
            >
              List
            </Button>
            <Button
              color="light"
              onClick={() => handleViewModeChange("grid")}
              active={viewMode === "grid"}
            >
              Grid
            </Button>
          </div>
        </MDBCol>
      </MDBRow>

      {/* Items Content */}
      {viewMode === "grid" ? (
        <MDBRow>
          {filteredItems.map((item) => (
            <MDBCol md="4" key={item.item_id}>
              <MDBCard className="order-card h-100">
                <MDBCardBody className="position-relative text-center">
                  <img
                    src={item.image_path || "https://via.placeholder.com/150"}
                    alt={item.name?.en}
                    className="img-fluid mb-3"
                    style={{ maxHeight: "150px", objectFit: "cover" }}
                  />
                  <h5 className="mb-1">{item.name?.en || "No Name"}</h5>
                  <small className="text-muted">
                    Price: SAR {item.price?.toFixed(2) || "0.00"}
                  </small>
                  <div className="text-center my-3">
                    <h6>
                      Category: {item.category_id?.name?.en || "No Category"}
                    </h6>
                  </div>
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    style={{ marginRight: "8px" }}
                    onClick={() => handleEditItemClick(item)}
                  >
                    Edit
                  </Button>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          ))}
        </MDBRow>
      ) : (
        <DataTable
          tableId="catalog-items"
          title={t("items")}
          selectionMode="index"
          columns={columns}
          data={sortedItems}
          rowKey="item_id"
          emptyState="No items to display"
          onRowClick={handleEditItemClick}
          sort={{
            column: tableSortColumn,
            direction: sortDirection,
            onChange: ({ column, direction }) => {
              setTableSortColumn(column);
              setSortDirection(direction);
            },
          }}
          pagination={{
            page: currentPage,
            pageSize: ITEMS_PER_PAGE,
            total: totalItems,
            onPageChange: (newPage) => {
              if (newPage >= 1 && newPage <= totalPages) {
                setCurrentPage(newPage);
              }
            },
            onPageSizeChange: () => {},
            pageSizeOptions: [10, 20, 50],
            showLabel: t("Show", { defaultValue: "Show" }),
            ofLabel: t("of", { defaultValue: "Of" }),
            resultsLabel: t("results", { defaultValue: "results" }),
            previousLabel: t("previous"),
            nextLabel: t("next"),
          }}
        />
      )}
      <ConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        title={modalData.title}
        message={modalData.message}
        action={modalData.action}
        actionData={modalData.actionData}
        visibility={modalData.visibility}
      />
    </MDBContainer>
  );
}

export default Items;
